import { Button, EmptyState, FieldBlock, Input, LoadingState, Textarea, useToast, AnchoredOverlay } from '@orbind/ui'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { ErroDaConversa } from '../features/chat/estados.tsx'
import {
  TIPOS_DE_CANAL,
  TOGGLE_CONTATOS_EXTERNOS,
  estaSilenciada,
  nomeVisivelDoCanal,
  podeEscrever,
  textoDigitando,
  type CanalDeChat,
  type MensagemDoCanal,
  type TipoDeAnexo,
  type TipoDeCanal,
} from '@orbind/core'
import { ExplorerBody, useExplorer, useExplorerConfig } from '../shell/explorer.tsx'
import { ExplorerDrawer } from './chat-shared.tsx'
import { usePresence } from '../features/presence/PresenceProvider.tsx'
import { Composer } from '../features/chat/Composer.tsx'
import { GroupInfo } from '../features/chat/GroupInfo.tsx'
import { MenuDaConversa } from '../features/chat/MenuDaConversa.tsx'
import { MessageItem } from '../features/chat/MessageItem.tsx'
import { abreDiaNovo, rotuloDoDia } from '../features/chat/dia-da-conversa.ts'
import type { RascunhoDeEstilo } from '../features/chat/MeusEstilos.tsx'
import { NovaConversa } from '../features/chat/NovaConversa.tsx'
import { useVideochamada } from '../features/chat/Videochamada.tsx'
import {
  FUNDOS,
  PersonalizarComunicacao,
  useCorDosPaineis,
  useCoresDosBaloes,
  type IdDoFundo,
} from '../features/chat/PersonalizarComunicacao.tsx'
import {
  avatarDoAutor,
  useAcoesDoChat,
  useAvisarDigitando,
  useCanais,
  useCanal,
  useChatAoVivo,
  useBuscaDeMensagens,
  useContatos,
  useDigitando,
  useEstilosDoChat,
  useMencoes,
  useMensagens,
  urlDaFoto,
} from '../features/chat/useChat.ts'
import { useFeatureToggle } from '../lib/feature-toggle.ts'
import './channels.css'
import '../features/chat/chat.css'

/**
 * Comunicação — lista de conversas no explorer do SHELL + conversa no `<main>`.
 *
 * Dados REAIS desde a SPEC-20260808-1500: canais, mensagens, anexos, reações,
 * fixadas e favoritas vêm da API, e a conversa se atualiza sozinha pelo canal
 * de presença.
 */
export function ChannelsPage() {
  const navigate = useNavigate()
  const { id: selectedId = null } = useParams()
  const toast = useToast()
  const { search, chip } = useExplorer()
  const { eu, membros: pessoasDoWorkspace, presencas } = usePresence()

  const canais = useCanais()
  const detalhe = useCanal(selectedId)
  const mensagens = useMensagens(selectedId)
  const acoes = useAcoesDoChat(selectedId)
  useChatAoVivo(selectedId)
  const digitando = useDigitando(selectedId)
  const avisarDigitando = useAvisarDigitando(selectedId)
  const externosLigados = useFeatureToggle(TOGGLE_CONTATOS_EXTERNOS)
  const contatos = useContatos(externosLigados)
  const convitesRecebidos = (contatos.data?.convites ?? []).filter((c) => c.direcao === 'recebido').length

  const [criarAberto, setCriarAberto] = useState(false)
  const [novaConversa, setNovaConversa] = useState<'inicio' | 'convites' | null>(null)
  /**
   * Prévia ao vivo do estilo em edição (pedido do usuário, 2026-09-17): enquanto
   * a pessoa mexe nas opções, a conversa aberta já mostra o resultado. Só
   * aparência na tela — nada é gravado até ela salvar.
   */
  const [previaDeEstilo, setPreviaDeEstilo] = useState<RascunhoDeEstilo | null>(null)
  const [form, setForm] = useState({ name: '', description: '', kind: 'time' as TipoDeCanal })
  const [respondendo, setRespondendo] = useState<MensagemDoCanal | null>(null)
  const [infoAberto, setInfoAberto] = useState(false)
  const [encaminhando, setEncaminhando] = useState<{ msg: MensagemDoCanal; ancora: HTMLElement } | null>(null)
  /** Menu do botão direito na lista (pedido 2026-09-14) — a âncora é um ponto
      invisível no lugar do clique, não um elemento de verdade da tela. */
  const [menuConversa, setMenuConversa] = useState<{ canal: CanalDeChat; ancora: HTMLElement } | null>(null)
  const fimDaLista = useRef<HTMLDivElement>(null)
  // Aparência vem do preset ATIVO de "Meus Estilos" (SPEC-20260917-0010); sem
  // preset nenhum (conta nova, nada migrado), cai no padrão do tema.
  const { ativo: estiloAtivo, criar: criarEstilo, atualizar: atualizarEstilo } = useEstilosDoChat()
  // Com prévia aberta, ela manda na tela; sem prévia, vale o estilo ativo.
  const aparencia = previaDeEstilo ?? estiloAtivo
  const corDosPaineis = aparencia?.backgroundColor ?? null
  useCorDosPaineis(corDosPaineis)
  const fundo = (FUNDOS.some((f) => f.id === aparencia?.wallpaper) ? aparencia!.wallpaper : 'orbind') as IdDoFundo
  useCoresDosBaloes(aparencia?.myBubbleColor ?? null, aparencia?.otherBubbleColor ?? null)

  /** Menu rápido (engrenagem) edita o preset ativo; sem nenhum ainda, cria um. */
  const definirCorDosPaineis = (cor: string | null) => {
    if (estiloAtivo) atualizarEstilo.mutate({ id: estiloAtivo.id, backgroundColor: cor })
    else criarEstilo.mutate({ name: 'Meu estilo', backgroundColor: cor, wallpaper: 'orbind', myBubbleColor: null, otherBubbleColor: null })
  }
  const definirFundo = (novoFundo: IdDoFundo) => {
    if (estiloAtivo) atualizarEstilo.mutate({ id: estiloAtivo.id, wallpaper: novoFundo })
    else
      criarEstilo.mutate({ name: 'Meu estilo', backgroundColor: null, wallpaper: novoFundo, myBubbleColor: null, otherBubbleColor: null })
  }

  const canal = detalhe.data?.channel ?? null
  const membros = detalhe.data?.members ?? []
  const lista = mensagens.data ?? []

  const nomesDosMembros = useMemo(() => membros.map((m) => m.name), [membros])
  const chamada = useVideochamada()
  const outraPessoa = membros.find((m) => m.memberId !== eu) ?? null

  /**
   * Avisa quem escreveu que a outra pessoa não vai ver agora
   * (SPEC-20260924-0924, pedido do usuário).
   *
   * Sem isto, a mensagem sai e o silêncio do outro lado passa por desprezo —
   * quando é só alguém almoçando. A mensagem foi entregue e está lá; o que
   * espera é o aviso dela, que sai quando ela voltar.
   *
   * Só em conversa de PESSOA (direta): em canal de time sempre há alguém
   * ausente, e o aviso viraria ruído em toda mensagem.
   */
  const avisarSeAusente = () => {
    if (!outraPessoa || canal?.kind !== 'pessoa') return
    const dela = presencas.get(outraPessoa.memberId)
    const estado = dela?.estado ?? 'desconectado'
    if (estado === 'disponivel' || estado === 'no-computador') return
    const nome = outraPessoa.name.split(' ')[0] ?? outraPessoa.name
    toast.push({
      tone: 'info',
      message:
        estado === 'offline' || estado === 'desconectado'
          ? `${nome} está offline. Vai receber o aviso quando voltar.`
          : `${nome} está ausente. Vai receber o aviso quando voltar.`,
    })
  }

  const titulo = canal
    ? nomeVisivelDoCanal(canal, membros, eu ?? '')
    : ''

  // Ao trocar de conversa, a citação pendente não pode viajar junto.
  useEffect(() => {
    setRespondendo(null)
    setInfoAberto(false)
  }, [selectedId])

  // Abrir a conversa (ou receber mensagem com ela aberta) é ler: sem isto o
  // contador de não lidas ficava na lista mesmo com tudo à vista.
  const ultimaId = lista[lista.length - 1]?.id ?? null
  const marcarLida = acoes.marcarLida.mutate
  useEffect(() => {
    if (!selectedId || !ultimaId) return
    const naLista = canais.data?.find((c) => c.id === selectedId)
    if (naLista && naLista.naoLidas === 0) return
    marcarLida({ canal: selectedId, messageId: ultimaId })
  }, [selectedId, ultimaId, canais.data, marcarLida])

  // Conversa abre no fim, onde está o que acabou de chegar.
  useEffect(() => {
    fimDaLista.current?.scrollIntoView({ block: 'end' })
  }, [lista.length, selectedId])

  /**
   * "Nova conversa" só na aba Tudo (pedido do usuário, 2026-09-16): nas outras
   * abas a pessoa está filtrando o que já existe, e o convite para criar só
   * atrapalhava. Vale para o `+` do topo e para os botões dos estados vazios.
   */
  const naAbaTudo = chip === '' || chip === 'Tudo'

  // Menções é um HISTÓRICO de mensagens que me chamaram, e não um filtro de
  // conversas com menção não lida: antes, abrir a conversa marcava como lida e
  // a aba esvaziava — a menção sumia justo depois de vista (print 2026-09-14).
  const vendoMencoes = chip === 'Menções'
  const mencoes = useMencoes(vendoMencoes)

  // "Arquivadas" é uma vista à parte, no molde de Menções: some da lista de
  // sempre (o servidor já filtra) e só aparece aqui, dentro do próprio chip.
  const vendoArquivadas = chip === 'Arquivadas'
  const arquivadas = useCanais({ arquivadas: true, ligado: vendoArquivadas })

  const linhas = useMemo(() => {
    const busca = search.trim().toLowerCase()
    return (canais.data ?? []).filter((c) => {
      if (chip === 'Não lidas' && c.naoLidas === 0) return false
      if (chip === 'Canais' && c.kind === 'pessoa') return false
      if (busca === '') return true
      return c.name.toLowerCase().includes(busca)
    })
  }, [canais.data, search, chip])

  const totalNaoLidas = (canais.data ?? []).reduce((t, c) => t + c.naoLidas, 0)

  // Com texto na pesquisa a lista vira busca, como no mensageiro de referência:
  // conversas cujo NOME casa em cima, mensagens que contêm o termo embaixo.
  const termoDeBusca = search.trim()
  const buscando = termoDeBusca.length >= 2
  const achadas = useBuscaDeMensagens(buscando ? termoDeBusca : '')

  const totalDeMencoes = mencoes.data?.length ?? 0
  const mencoesNovas = (mencoes.data ?? []).filter((m) => m.naoLida).length

  useExplorerConfig({
    footer: canais.isPending
      ? 'Carregando conversas…'
      : vendoMencoes && !buscando
      ? `${totalDeMencoes} ${totalDeMencoes === 1 ? 'menção' : 'menções'} · ${mencoesNovas} ${
          mencoesNovas === 1 ? 'nova' : 'novas'
        }`
      : vendoArquivadas && !buscando
      ? `${(arquivadas.data ?? []).length} ${(arquivadas.data ?? []).length === 1 ? 'conversa arquivada' : 'conversas arquivadas'}`
      : buscando
      ? `${linhas.length} ${linhas.length === 1 ? 'conversa' : 'conversas'} · ${achadas.data?.length ?? 0} ${
          achadas.data?.length === 1 ? 'mensagem encontrada' : 'mensagens encontradas'
        }`
      : `${linhas.length} ${linhas.length === 1 ? 'conversa' : 'conversas'} · ${totalNaoLidas} não lidas`,
    // O `+` abre "Nova conversa" (pessoas e canais), como no mensageiro de
    // referência; o formulário de canal é um dos itens de lá. Fora da aba Tudo
    // ele nem aparece.
    onCreate: naAbaTudo ? () => setNovaConversa('inicio') : undefined,
  })

  // Falha de envio nunca é silenciosa: foi assim que a enquete "sumia".
  const avisarFalha = (erro: Error) => toast.push({ tone: 'danger', message: erro.message })

  const irParaMensagem = (channelId: string, messageId: string) => {
    if (channelId !== selectedId) navigate(`/channels/${channelId}`)
    // Vindo da busca, a conversa ainda está carregando: tenta por até ~3s em
    // vez de um único quadro, senão o clique abria a conversa e não achava nada.
    let tentativas = 0
    const procurar = () => {
      const alvo = document.getElementById(`msg-${messageId}`)
      if (!alvo) {
        if (++tentativas < 20) setTimeout(procurar, 150)
        return
      }
      alvo.scrollIntoView({ block: 'center', behavior: 'smooth' })
      alvo.classList.add('ch-msgrow--destacada')
      setTimeout(() => alvo.classList.remove('ch-msgrow--destacada'), 1600)
    }
    setTimeout(procurar, 120)
  }

  const acoesDaMensagem = {
    onResponder: (m: MensagemDoCanal) => setRespondendo(m),
    onResponderEmParticular: (m: MensagemDoCanal) => {
      if (!m.autor) return
      acoes.abrirPrivado.mutate(m.autor.memberId, {
        onSuccess: (dados) => {
          navigate(`/channels/${(dados as { channelId: string }).channelId}`)
          toast.push({ tone: 'info', message: `Conversa com ${m.autor?.name} aberta.` })
        },
      })
    },
    onCopiar: (m: MensagemDoCanal) => {
      void navigator.clipboard?.writeText(m.corpo)
      toast.push({ tone: 'success', message: 'Mensagem copiada.' })
    },
    onReagir: (m: MensagemDoCanal, emoji: string) => acoes.reagir.mutate({ messageId: m.id, emoji }),
    onVotar: (m: MensagemDoCanal, opcao: number) =>
      acoes.votar.mutate(
        { messageId: m.id, opcao, meuNome: pessoasDoWorkspace.find((p) => p.id === eu)?.name ?? 'Você' },
        { onError: avisarFalha },
      ),
    onEncaminhar: (m: MensagemDoCanal) => {
      const ancora = document.getElementById(`msg-${m.id}`)
      if (ancora) setEncaminhando({ msg: m, ancora })
    },
    onApagar: (m: MensagemDoCanal) => acoes.apagar.mutate(m.id),
    onFixar: (m: MensagemDoCanal) => acoes.fixar.mutate(m.id),
    onFavoritar: (m: MensagemDoCanal) => acoes.favoritar.mutate(m.id),
    onIrParaCitada: (messageId: string) => irParaMensagem(selectedId!, messageId),
  }

  const linhaDeCanal = (c: (typeof linhas)[number]) => (
    <button
      key={c.id}
      type="button"
      className={`ch-row ch-lista${c.id === selectedId ? ' ch-row--on' : ''}`}
      onClick={() => navigate(`/channels/${c.id}`)}
      onContextMenu={(e) => {
        e.preventDefault()
        // A âncora do menu é um ponto invisível no lugar do clique — não há
        // "o botão que foi clicado" num botão direito, é a posição do mouse.
        const fantasma = document.createElement('div')
        fantasma.style.position = 'fixed'
        fantasma.style.left = `${e.clientX}px`
        fantasma.style.top = `${e.clientY}px`
        document.body.appendChild(fantasma)
        setMenuConversa({ canal: c, ancora: fantasma })
      }}
    >
      <span className="ch-avatarwrap">
        <span className="ch-avatar">
          {urlDaFoto(c.foto) ? (
            <img className="ch-avatar__img" src={urlDaFoto(c.foto)!} alt="" />
          ) : (
            <i className={c.kind === 'pessoa' ? 'fas fa-user' : 'fas fa-users'} aria-hidden />
          )}
        </span>
      </span>
      <span className="ch-row__main">
        <span className="ch-row__top">
          <span className="ch-row__title">{c.name}</span>
          {c.fixadaEm ? <i className="fas fa-thumbtack ch-row__fixada" title="Conversa fixada" aria-hidden /> : null}
          <span className="ch-row__time">{horaCurta(c.ultimaMensagemEm)}</span>
        </span>
        <span className="ch-row__bottom">
          <span className="ch-row__preview">{c.ultimaMensagem ?? 'Nenhuma mensagem ainda'}</span>
          {estaSilenciada(c.silenciadaAte) ? (
            <i className="fas fa-bell-slash ch-row__silenciada" title="Conversa silenciada" aria-label="Conversa silenciada" />
          ) : null}
          {c.favoritaEm ? (
            <i className="fas fa-heart ch-row__favorita" title="Conversa favorita" aria-label="Conversa favorita" />
          ) : null}
          {c.mencoesNaoLidas > 0 ? (
            <span className="ch-row__unread" title="Você foi mencionado" aria-label="Você foi mencionado">
              @
            </span>
          ) : null}
          {c.naoLidas > 0 || c.marcadaNaoLida ? (
            <span
              className="ch-row__unread"
              aria-label={c.naoLidas > 0 ? `${c.naoLidas} mensagens não lidas` : 'Marcada como não lida'}
            >
              {c.naoLidas > 0 ? c.naoLidas : ''}
            </span>
          ) : null}
        </span>
      </span>
    </button>
  )

  return (
    <>
      <ExplorerBody>
        {/* Convite de contato pendente fica visível sem abrir nada. */}
        {convitesRecebidos > 0 ? (
          <button type="button" className="ch-row ch-lista" onClick={() => setNovaConversa('convites')}>
            <span className="ch-avatarwrap">
              <span className="ch-avatar">
                <i className="fas fa-envelope" aria-hidden />
              </span>
            </span>
            <span className="ch-row__main">
              <span className="ch-row__top">
                <span className="ch-row__title">Convites de contato</span>
              </span>
              <span className="ch-row__bottom">
                <span className="ch-row__preview">
                  {convitesRecebidos === 1 ? 'Uma pessoa quer conversar com você' : `${convitesRecebidos} pessoas querem conversar com você`}
                </span>
                <span className="ch-row__unread" aria-label={`${convitesRecebidos} convites`}>
                  {convitesRecebidos}
                </span>
              </span>
            </span>
          </button>
        ) : null}

        {canais.isPending ? (
          <div style={{ padding: 14 }}>
            <LoadingState rows={5} label="Carregando conversas" />
          </div>
        ) : canais.isError ? (
          // sem isto a lista aparecia VAZIA quando a rede falhava, e a pessoa
          // concluía que não tinha conversa nenhuma
          <div style={{ padding: '28px 16px' }}>
            <ErroDaConversa erro={canais.error} oQue="as conversas" onTentarDeNovo={() => canais.refetch()} />
          </div>
        ) : buscando ? (
          <>
            {linhas.length > 0 ? <span className="ch-info__rotulo ch-busca__rotulo">Conversas</span> : null}
            {linhas.map(linhaDeCanal)}

            <span className="ch-info__rotulo ch-busca__rotulo">Mensagens</span>
            {achadas.isPending ? (
              <div style={{ padding: '4px 14px' }}>
                <LoadingState rows={3} label="Buscando mensagens" />
              </div>
            ) : achadas.isError ? (
              <div style={{ padding: '4px 14px' }}>
                <ErroDaConversa erro={achadas.error} oQue="a busca" onTentarDeNovo={() => achadas.refetch()} />
              </div>
            ) : (achadas.data ?? []).length === 0 ? (
              <span className="ch-contact__sub ch-busca__vazio">
                Nenhuma mensagem com “{termoDeBusca}”.
              </span>
            ) : (
              (achadas.data ?? []).map((r) => (
                <button
                  key={r.messageId}
                  type="button"
                  className="ch-row ch-lista ch-busca__linha"
                  onClick={() => irParaMensagem(r.channelId, r.messageId)}
                >
                  <span className="ch-row__main">
                    <span className="ch-row__top">
                      <span className="ch-row__title">{r.canal}</span>
                      <span className="ch-row__time">{dataDaBusca(r.enviadaEm)}</span>
                    </span>
                    <span className="ch-row__bottom">
                      <span className="ch-row__preview">
                        {r.minha ? 'Você: ' : r.tipoDoCanal !== 'pessoa' && r.autor ? `${r.autor.split(' ')[0]}: ` : ''}
                        {comDestaque(r.trecho, termoDeBusca)}
                      </span>
                    </span>
                  </span>
                </button>
              ))
            )}
          </>
        ) : vendoMencoes ? (
          mencoes.isPending ? (
            <div style={{ padding: 14 }}>
              <LoadingState rows={4} label="Carregando menções" />
            </div>
          ) : mencoes.isError ? (
            <div style={{ padding: '28px 16px' }}>
              <EmptyState
                icon="fas fa-exclamation-triangle"
                title="Não foi possível carregar as menções"
                description="Verifique a conexão e tente de novo."
                action={
                  <Button variant="secondary" icon="fas fa-redo" onClick={() => void mencoes.refetch()}>
                    Tentar de novo
                  </Button>
                }
              />
            </div>
          ) : totalDeMencoes === 0 ? (
            <div style={{ padding: '28px 16px' }}>
              <EmptyState
                icon="fas fa-at"
                title="Ninguém mencionou você"
                description="Quando alguém escrever @ com o seu nome numa conversa, a mensagem aparece aqui."
              />
            </div>
          ) : (
            (mencoes.data ?? []).map((m) => (
              <button
                key={m.messageId}
                type="button"
                className={`ch-row ch-lista ch-busca__linha${m.channelId === selectedId ? ' ch-row--on' : ''}`}
                onClick={() => irParaMensagem(m.channelId, m.messageId)}
              >
                <span className="ch-avatarwrap">
                  <span className="ch-avatar">
                    <i className="fas fa-at" aria-hidden />
                  </span>
                </span>
                <span className="ch-row__main">
                  <span className="ch-row__top">
                    <span className="ch-row__title">{m.canal}</span>
                    <span className="ch-row__time">{dataDaBusca(m.enviadaEm)}</span>
                  </span>
                  <span className="ch-row__bottom">
                    <span className="ch-row__preview">
                      {m.tipoDoCanal !== 'pessoa' && m.autor ? `${m.autor.split(' ')[0]}: ` : ''}
                      {comDestaque(m.trecho, eu ? `@${nomeDoMembro(pessoasDoWorkspace, eu)}` : '')}
                    </span>
                    {m.naoLida ? (
                      <span className="ch-row__unread" title="Menção não lida" aria-label="Menção não lida">
                        @
                      </span>
                    ) : null}
                  </span>
                </span>
              </button>
            ))
          )
        ) : vendoArquivadas ? (
          arquivadas.isPending ? (
            <div style={{ padding: 14 }}>
              <LoadingState rows={3} label="Carregando arquivadas" />
            </div>
          ) : arquivadas.isError ? (
            <div style={{ padding: '28px 16px' }}>
              <ErroDaConversa
                erro={arquivadas.error}
                oQue="as conversas arquivadas"
                onTentarDeNovo={() => arquivadas.refetch()}
              />
            </div>
          ) : (arquivadas.data ?? []).length === 0 ? (
            <div style={{ padding: '28px 16px' }}>
              <EmptyState
                icon="fas fa-box-archive"
                title="Nenhuma conversa arquivada"
                description="Arquivar uma conversa some com ela desta lista, sem sair do grupo — desarquive quando quiser."
              />
            </div>
          ) : (
            (arquivadas.data ?? []).map(linhaDeCanal)
          )
        ) : linhas.length === 0 ? (
          <div style={{ padding: '28px 16px' }}>
            <EmptyState
              icon="fas fa-comments"
              title="Nenhuma conversa"
              description={
                naAbaTudo
                  ? 'Comece uma conversa com alguém da empresa ou crie um canal para a equipe.'
                  : 'Nenhuma conversa nesta aba. Volte para "Tudo" para ver todas.'
              }
              action={
                naAbaTudo ? (
                  <Button variant="secondary" icon="fas fa-plus" onClick={() => setNovaConversa('inicio')}>
                    Nova conversa
                  </Button>
                ) : undefined
              }
            />
          </div>
        ) : (
          linhas.map(linhaDeCanal)
        )}
      </ExplorerBody>

      {menuConversa && eu ? (
        <MenuDaConversa
          canal={menuConversa.canal}
          ancora={menuConversa.ancora}
          euMemberId={eu}
          estaAberta={menuConversa.canal.id === selectedId}
          onSairDaAberta={() => navigate('/channels')}
          onClose={() => {
            menuConversa.ancora.remove()
            setMenuConversa(null)
          }}
        />
      ) : null}

      {novaConversa ? (
        <NovaConversa
          telaInicial={novaConversa}
          onFechar={() => setNovaConversa(null)}
          onNovoCanal={() => {
            setNovaConversa(null)
            setForm({ name: '', description: '', kind: 'time' })
            setCriarAberto(true)
          }}
          onAbrirConversa={(memberId, nome) =>
            acoes.abrirPrivado.mutate(memberId, {
              onSuccess: (dados) => {
                setNovaConversa(null)
                navigate(`/channels/${(dados as { channelId: string }).channelId}`)
              },
              onError: () => toast.push({ tone: 'danger', message: `Não foi possível abrir a conversa com ${nome}.` }),
            })
          }
        />
      ) : null}

      {criarAberto ? (
        <ExplorerDrawer
          title="Novo canal"
          subtitle="Comunicação da equipe"
          onClose={() => setCriarAberto(false)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setCriarAberto(false)}>
                Cancelar
              </Button>
              <Button
                disabled={form.name.trim() === '' || acoes.criarCanal.isPending}
                onClick={() =>
                  acoes.criarCanal.mutate(
                    {
                      name: form.name.trim(),
                      kind: form.kind,
                      description: form.description.trim() || null,
                    },
                    {
                      onSuccess: (dados) => {
                        setCriarAberto(false)
                        const criado = (dados as { channel?: { id: string } }).channel
                        if (criado) navigate(`/channels/${criado.id}`)
                        toast.push({ tone: 'success', message: `Canal “${form.name.trim()}” criado.` })
                      },
                    },
                  )
                }
              >
                Criar canal
              </Button>
            </>
          }
        >
          <div className="ch-drawer__form">
            <FieldBlock label="Nome do canal" required>
              {({ id }) => (
                <Input
                  id={id}
                  value={form.name}
                  placeholder="Ex.: Operações"
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              )}
            </FieldBlock>

            <FieldBlock label="Descrição">
              {({ id }) => (
                <Textarea
                  id={id}
                  rows={2}
                  value={form.description}
                  placeholder="Para que serve este canal?"
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                />
              )}
            </FieldBlock>

            <FieldBlock label="Tipo do canal">
              {() => (
                <div className="ch-kindgrid" role="group" aria-label="Tipo do canal">
                  {TIPOS_DE_CANAL.filter((k) => k !== 'pessoa').map((kind) => (
                    <button
                      key={kind}
                      type="button"
                      className={`ch-kindbtn${form.kind === kind ? ' ch-kindbtn--on' : ''}`}
                      aria-pressed={form.kind === kind}
                      onClick={() => setForm((f) => ({ ...f, kind }))}
                    >
                      {kind}
                    </button>
                  ))}
                </div>
              )}
            </FieldBlock>
          </div>
        </ExplorerDrawer>
      ) : null}

      {/*
        Palco próprio: o `.shell__main` é `display: block`, então a conversa e o
        painel de dados precisam de um container flex aqui — mudar o shell
        afetaria todas as outras telas.
      */}
      <div className="ch-stage">
      {canal === null ? (
        <div className="ch-blank">
          <EmptyState
            icon="fas fa-comments"
            title="Selecione uma conversa"
            description="Escolha uma conversa no painel, ou comece uma nova."
            action={
              <Button icon="fas fa-plus" onClick={() => setNovaConversa('inicio')}>
                Nova conversa
              </Button>
            }
          />
        </div>
      ) : (
        <div className="ch-conv">
          {/* O nome no topo abre os dados do grupo — o gesto que o usuário pediu. */}
          <div className="ch-conv__head">
            <button type="button" className="ch-conv__opener" onClick={() => setInfoAberto(true)}>
              <span className="ch-avatarwrap">
                <span className="ch-avatar ch-avatar--lg">
                  {urlDaFoto(canal.foto) ? (
                    <img className="ch-avatar__img" src={urlDaFoto(canal.foto)!} alt="" />
                  ) : (
                    <i className={canal.kind === 'pessoa' ? 'fas fa-user' : 'fas fa-users'} aria-hidden />
                  )}
                </span>
              </span>
              <span className="ch-conv__id">
                <span className="ch-conv__title">{titulo}</span>
                <span className={`ch-conv__sub${digitando.length > 0 ? ' ch-conv__sub--digitando' : ''}`}>
                  {digitando.length > 0
                    ? textoDigitando(digitando, canal.kind === 'pessoa')
                    : canal.kind === 'pessoa'
                    ? 'Conversa direta'
                    : `${membros.length} participantes · ${membros.slice(0, 3).map((m) => m.name.split(' ')[0]).join(', ')}${membros.length > 3 ? ' e outros' : ''}`}
                </span>
              </span>
            </button>
            {/* Videochamada: só na conversa direta, com a outra pessoa. */}
            {canal.kind === 'pessoa' && outraPessoa ? (
              <>
                <button
                  type="button"
                  className="ch-iconbtn"
                  aria-label={`Chamada de voz com ${outraPessoa.name}`}
                  title={chamada.ocupado ? 'Você já está em uma chamada' : 'Chamada de voz (sem câmera)'}
                  disabled={chamada.ocupado}
                  onClick={() =>
                    chamada.ligar(
                      { memberId: outraPessoa.memberId, nome: outraPessoa.name, foto: avatarDoAutor(outraPessoa) },
                      canal.id,
                      { video: false },
                    )
                  }
                >
                  <i className="fas fa-phone-alt" aria-hidden />
                </button>
                <button
                  type="button"
                  className="ch-iconbtn"
                  aria-label={`Videochamada com ${outraPessoa.name}`}
                  title={chamada.ocupado ? 'Você já está em uma chamada' : 'Videochamada'}
                  disabled={chamada.ocupado}
                  onClick={() =>
                    chamada.ligar(
                      { memberId: outraPessoa.memberId, nome: outraPessoa.name, foto: avatarDoAutor(outraPessoa) },
                      canal.id,
                      { video: true },
                    )
                  }
                >
                  <i className="fas fa-video" aria-hidden />
                </button>
              </>
            ) : null}
          </div>

          {/* O fundo é uma camada própria atrás da lista: o desenho fica parado
              enquanto as mensagens rolam por cima dele. */}
          <div className={`ch-fundo ch-fundo--${fundo}`}>
          <div className="ch-msgs">
            {mensagens.isPending ? (
              <LoadingState rows={4} label="Carregando mensagens" />
            ) : mensagens.isError ? (
              // o caso que mais enganava: canal sem permissão caía no vazio e
              // dizia "Nenhuma mensagem ainda", como se a conversa estivesse
              // limpa em vez de fechada para quem olha
              <div style={{ margin: 'auto' }}>
                <ErroDaConversa
                  erro={mensagens.error}
                  oQue="as mensagens"
                  onTentarDeNovo={() => mensagens.refetch()}
                />
              </div>
            ) : lista.length === 0 ? (
              <div style={{ margin: 'auto' }}>
                <EmptyState
                  icon="fas fa-comments"
                  title="Nenhuma mensagem ainda"
                  description="Escreva a primeira mensagem desta conversa."
                />
              </div>
            ) : (
              lista.map((m, indice) => {
                const anterior = lista[indice - 1]
                // selo do dia, como no WhatsApp (SPEC-20261007-1615)
                const diaNovo = abreDiaNovo(m.enviadaEm, anterior?.enviadaEm)
                const primeiraDoBloco =
                  diaNovo ||
                  !anterior ||
                  anterior.autor?.memberId !== m.autor?.memberId ||
                  new Date(m.enviadaEm).getTime() - new Date(anterior.enviadaEm).getTime() > 5 * 60 * 1000
                return (
                  <Fragment key={m.id}>
                  {diaNovo ? (
                    <div className="ch-daysep" role="separator">
                      <span>{rotuloDoDia(new Date(m.enviadaEm))}</span>
                    </div>
                  ) : null}
                  <MessageItem
                    mensagem={m}
                    euMemberId={eu ?? ''}
                    meuPapel={canal.meuPapel}
                    primeiraDoBloco={primeiraDoBloco}
                    conversaDireta={canal.kind === 'pessoa'}
                    nomesMencionaveis={nomesDosMembros}
                    acoes={acoesDaMensagem}
                  />
                  </Fragment>
                )
              })
            )}
            <div ref={fimDaLista} />
          </div>
          </div>

          <Composer
            respondendo={respondendo}
            onCancelarResposta={() => setRespondendo(null)}
            desabilitado={!podeEscrever(canal)}
            avisoDesabilitado="Este canal é somente leitura."
            membros={membros}
            onDigitar={avisarDigitando}
            onEnviar={(texto, mencoes) => {
              acoes.enviar.mutate(
                { body: texto, replyToMessageId: respondendo?.id ?? null, mencoes },
                { onError: avisarFalha },
              )
              avisarSeAusente()
              setRespondendo(null)
            }}
            onEnviarArquivo={(arquivo: File, tipo: TipoDeAnexo) => {
              acoes.enviar.mutate(
                { arquivo, tipoAnexo: tipo, replyToMessageId: respondendo?.id ?? null },
                { onError: avisarFalha },
              )
              setRespondendo(null)
            }}
            onEnviarEstruturado={(dados) => {
              acoes.anexarEstruturado.mutate(dados, { onError: avisarFalha })
              setRespondendo(null)
            }}
          />
        </div>
      )}

      {infoAberto && canal ? (
        <GroupInfo
          canal={canal}
          membros={membros}
          onFechar={() => setInfoAberto(false)}
          onAlterar={(dados) => acoes.alterarCanal.mutate(dados)}
          onAdicionar={(ids) => acoes.adicionarMembros.mutate(ids)}
          onRemover={(id) => acoes.removerMembro.mutate(id)}
          onIrParaMensagem={irParaMensagem}
          onTrocarFoto={(arquivo) =>
            acoes.trocarFoto.mutate(arquivo, {
              onSuccess: () => toast.push({ tone: 'success', message: 'Foto do grupo atualizada.' }),
              onError: avisarFalha,
            })
          }
          onRemoverFoto={() => acoes.removerFoto.mutate(undefined, { onError: avisarFalha })}
          onSilenciar={(duracao) =>
            acoes.silenciar.mutate(duracao, {
              onSuccess: () =>
                toast.push({
                  tone: 'success',
                  message: duracao ? 'Conversa silenciada.' : 'Notificações reativadas.',
                }),
              onError: avisarFalha,
            })
          }
        />
      ) : null}
      </div>

      {/* Engrenagem ao lado do "+": cor dos painéis e desenho do fundo. */}
      <PersonalizarComunicacao
        cor={corDosPaineis}
        onCor={definirCorDosPaineis}
        fundo={fundo}
        onFundo={definirFundo}
        onPrevia={setPreviaDeEstilo}
      />

      {/* Encaminhar: escolher o destino entre as conversas de que participo. */}
      {encaminhando ? (
        <AnchoredOverlay
          anchor={encaminhando.ancora}
          onClose={() => setEncaminhando(null)}
          prefer="esquerda"
          role="dialog"
          aria-label="Encaminhar para"
          className="ch-menu"
        >
          <span className="ch-drawer__sub ch-menu__label">Encaminhar para</span>
          {(canais.data ?? [])
            .filter((c) => c.id !== selectedId)
            .map((c) => (
              <button
                key={c.id}
                type="button"
                className="ch-more__item"
                onClick={() => {
                  acoes.encaminhar.mutate(
                    { paraCanal: c.id, messageId: encaminhando.msg.id },
                    { onSuccess: () => toast.push({ tone: 'success', message: `Encaminhada para ${c.name}.` }) },
                  )
                  setEncaminhando(null)
                }}
              >
                <i className={c.kind === 'pessoa' ? 'fas fa-user' : 'fas fa-users'} aria-hidden />
                {c.name}
              </button>
            ))}
          {/* Ainda dá para encaminhar a quem não tem conversa aberta comigo. */}
          {pessoasDoWorkspace
            .filter((p) => p.id !== eu)
            .slice(0, 8)
            .map((p) => (
              <button
                key={p.id}
                type="button"
                className="ch-more__item"
                onClick={() => {
                  acoes.abrirPrivado.mutate(p.id, {
                    onSuccess: (dados) => {
                      acoes.encaminhar.mutate({
                        paraCanal: (dados as { channelId: string }).channelId,
                        messageId: encaminhando.msg.id,
                      })
                      toast.push({ tone: 'success', message: `Encaminhada para ${p.name}.` })
                    },
                  })
                  setEncaminhando(null)
                }}
              >
                <i className="fas fa-user" aria-hidden />
                {p.name}
              </button>
            ))}
        </AnchoredOverlay>
      ) : null}
    </>
  )
}

/** Hora da prévia na lista: "09:03" hoje, "05/08" antes — o formato do protótipo. */
function horaCurta(iso: string | null): string {
  if (!iso) return ''
  const data = new Date(iso)
  const hoje = new Date()
  return data.toDateString() === hoje.toDateString()
    ? data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
}

/** Data da busca no formato do mensageiro: hora hoje, "Ontem", depois a data. */
function dataDaBusca(iso: string): string {
  const data = new Date(iso)
  const hoje = new Date()
  const ontem = new Date(hoje)
  ontem.setDate(hoje.getDate() - 1)
  if (data.toDateString() === hoje.toDateString()) {
    return data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  }
  if (data.toDateString() === ontem.toDateString()) return 'Ontem'
  return data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/** Destaca o termo buscado dentro da prévia (sem diferenciar maiúsculas). */
function comDestaque(texto: string, termo: string) {
  if (termo === '') return texto
  const escapado = termo.replace(/[.*+?^$()|[\]{}\\]/g, '\\$&')
  const partes = texto.split(new RegExp(`(${escapado})`, 'gi'))
  return partes.map((parte, i) =>
    parte.toLowerCase() === termo.toLowerCase() ? (
      <strong key={i} className="ch-busca__destaque">
        {parte}
      </strong>
    ) : (
      parte
    ),
  )
}

/** Meu nome entre as pessoas do workspace (para destacar o "@Nome" da menção). */
function nomeDoMembro(pessoas: readonly { id: string; name: string }[], id: string): string {
  return pessoas.find((p) => p.id === id)?.name ?? ''
}

/** Reexportado para o painel de membros abrir conversa direta. */
export { avatarDoAutor }
